"use client";

import * as React from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { cn } from "../lib/cn";
import { getExperienceTypeMeta } from "./ExperienceTypeBadge";

/**
 * AnimatedExperienceTypeIcon — "live icon" hover treatment for the five AI
 * Experience type glyphs. While `active`, each type plays a signature motion
 * layered *around* its anchored lucide icon (the icon itself never swaps out,
 * so the type affordance is preserved at all times):
 *
 *   - **podcast** (Headphones) — bob + orange equalizer bars + ♪ notes drifting up
 *   - **video**   (Video)      — tracking-pan + blinking red REC dot + film frames streaming out
 *   - **quest**   (Compass)    — needle "searching" wobble + a dotted trail to a popping ⚑ flag
 *   - **expert**  (Brain)      — heartbeat pulse + chat typing-indicator dots above
 *   - **note**    (FileText)   — paper sway + ✎ pencil scribbling + ✦ AI sparkles
 *
 * No two types share a movement, glyph, or motion direction. Every layer is
 * transform/opacity-only and mounts **only while `active`**, so idle cards in a
 * large grid pay zero animation cost. `prefers-reduced-motion` falls back to the
 * plain static icon, and the whole thing is `aria-hidden` (decorative — pair it
 * with a text label or <ExperienceTypeBadge> for the accessible name).
 *
 * The icon for each type comes from the same source of truth as
 * <ExperienceTypeBadge> (`getExperienceTypeMeta`), so the static and animated
 * surfaces can never drift apart.
 *
 * @example
 * function Card() {
 *   const [hovered, setHovered] = React.useState(false);
 *   return (
 *     <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
 *       <AnimatedExperienceTypeIcon type="PODCAST" active={hovered} size="md" />
 *     </div>
 *   );
 * }
 */

export type AnimatedExperienceType = "podcast" | "video" | "quest" | "expert" | "note";

/**
 * Normalize a raw experience type to its animation variant, or `null` if the
 * type has no animation. Folds API keys (`PODCAST`, `AI_VIDEO`), wizard ids
 * (`ai-note`, `ai-expert`), label aliases (`Quiz` → quest, `notes`/`document`
 * → note), and bare lowercase alike.
 */
export function getAnimatedExperienceType(type: string): AnimatedExperienceType | null {
  switch ((type ?? "").toLowerCase().replace(/[^a-z]/g, "")) {
    case "podcast":
      return "podcast";
    case "video":
    case "aivideo":
      return "video";
    case "quest":
    case "quiz":
      return "quest";
    case "expert":
    case "aiexpert":
      return "expert";
    case "note":
    case "notes":
    case "ainote":
    case "document":
      return "note";
    default:
      return null;
  }
}

/**
 * Signature idle-loop the icon itself plays while active. Each type owns a
 * distinct motion — bob / pan / wobble / heartbeat / sway — so no two read alike.
 */
const SIGNATURES: Record<
  AnimatedExperienceType,
  { animate: Record<string, number[]>; duration: number }
> = {
  podcast: { animate: { rotate: [0, -7, 7, 0] }, duration: 1.15 },
  video: { animate: { x: [0, -1.5, 1.5, 0], rotate: [0, -3, 3, 0] }, duration: 2.2 },
  quest: { animate: { rotate: [0, 22, -16, 8, 0] }, duration: 1.8 },
  expert: { animate: { scale: [1, 1.09, 1, 1.05, 1] }, duration: 1.4 },
  note: { animate: { rotate: [0, -3, 2, 0] }, duration: 1.6 },
};

/**
 * Background class for the per-type accent element (EQ bars / REC dot / trail /
 * typing dots). Tones match <ExperienceTypeBadge>; override via `accentClassName`.
 * The video REC dot stays red by recording-light convention.
 */
const ACCENTS: Record<AnimatedExperienceType, string> = {
  podcast: "bg-orange-500",
  video: "bg-red-500",
  quest: "bg-violet-500",
  expert: "bg-emerald-500",
  note: "bg-sky-500",
};

interface Twinkle {
  style: React.CSSProperties;
  size: number;
  delay: number;
}

interface NoteDrift {
  char: string;
  x: number;
  size: number;
  delay: number;
}

interface SizeSpec {
  icon: string;
  lift: number;
  ripple: string | null;
  drift: number;
  eq: { bars: number[]; bar: string; gap: string; offset: string };
  rec: string;
  frame: string;
  frameDrift: { x: number; y: number };
  frameDelays: number[];
  trailDots: { x: number; y: number; tIn: number }[];
  trailDot: string;
  flag: { x: number; y: number; size: number };
  typingOffset: string;
  typingGap: string;
  typingDot: string;
  typingBounce: number;
  notes: NoteDrift[];
  sparkles: Twinkle[];
  pencilPos: string;
  pencilSize: number;
}

/**
 * One spec object per size — every offset, bar height, particle path, and font
 * size lives here. Tuning the animation = editing numbers, no JSX changes.
 */
const SIZES: Record<"md" | "sm", SizeSpec> = {
  md: {
    icon: "h-8 w-8",
    lift: -4,
    ripple: "-inset-4 rounded-2xl",
    drift: -44,
    eq: { bars: [9, 13, 7, 11], bar: "w-[3px]", gap: "gap-[2px]", offset: "-bottom-3" },
    rec: "-top-1 -right-1 h-1.5 w-1.5",
    frame: "left-full top-1 h-[5px] w-[7px] rounded-[1px]",
    frameDrift: { x: 14, y: -12 },
    frameDelays: [0, 0.65],
    trailDots: [
      { x: -13, y: -7, tIn: 0.1 },
      { x: -6, y: -13, tIn: 0.24 },
      { x: 2, y: -17, tIn: 0.38 },
    ],
    trailDot: "h-1 w-1",
    flag: { x: 10, y: -23, size: 11 },
    typingOffset: "-top-3",
    typingGap: "gap-[3px]",
    typingDot: "h-1 w-1",
    typingBounce: -3,
    notes: [
      { char: "♪", x: -13, size: 13, delay: 0 },
      { char: "♫", x: 7, size: 12, delay: 0.6 },
      { char: "♪", x: -3, size: 10, delay: 1.15 },
    ],
    sparkles: [
      { style: { top: -7, left: -6 }, size: 9, delay: 0 },
      { style: { top: -5, right: -8 }, size: 8, delay: 0.7 },
    ],
    pencilPos: "-bottom-1.5 -right-2",
    pencilSize: 11,
  },
  sm: {
    icon: "h-4 w-4",
    lift: -1.5,
    ripple: null,
    drift: -20,
    eq: { bars: [4, 6, 3], bar: "w-[2px]", gap: "gap-[1.5px]", offset: "-bottom-1.5" },
    rec: "-top-0.5 -right-0.5 h-1 w-1",
    frame: "left-full top-0.5 h-[3px] w-[4px] rounded-[0.5px]",
    frameDrift: { x: 9, y: -8 },
    frameDelays: [0, 0.65],
    trailDots: [
      { x: -7, y: -4, tIn: 0.1 },
      { x: -3, y: -8, tIn: 0.26 },
    ],
    trailDot: "h-[3px] w-[3px]",
    flag: { x: 3, y: -13, size: 8 },
    typingOffset: "-top-2",
    typingGap: "gap-[2px]",
    typingDot: "h-[2.5px] w-[2.5px]",
    typingBounce: -2,
    notes: [{ char: "♪", x: -7, size: 8, delay: 0 }],
    sparkles: [{ style: { top: -4, right: -5 }, size: 7, delay: 0 }],
    pencilPos: "-bottom-1 -right-1.5",
    pencilSize: 8,
  },
};

const BAR_DURATIONS = [0.8, 0.95, 0.7, 0.85];
// Quest trail runs as one shared timeline: dots step in, the flag pops, all fade.
const TRAIL_CYCLE = 2.4;

export interface AnimatedExperienceTypeIconProps {
  /** Raw experience type (e.g. "PODCAST"); any form `getAnimatedExperienceType` accepts. */
  type: string;
  /** Drives the animation — pass the card/row hover (or focus) state. */
  active: boolean;
  /** "md" = 32px icon (cover overlays) · "sm" = 16px (rows, badges). Default "md". */
  size?: "md" | "sm";
  /** Applied to the root — set the **text color** here; glyphs + ripple inherit it. */
  className?: string;
  /** Applied to the lucide icon (custom sizing, shadows). Overrides the `size` default. */
  iconClassName?: string;
  /** Background class for the accent element (EQ bars / REC dot / trail / typing dots). */
  accentClassName?: string;
  /** Rendered when `type` has no animation variant (default: nothing). */
  fallback?: React.ReactNode;
  style?: React.CSSProperties;
}

export function AnimatedExperienceTypeIcon({
  type,
  active,
  size = "md",
  className,
  iconClassName,
  accentClassName,
  fallback,
  style,
}: AnimatedExperienceTypeIconProps) {
  const reducedMotion = useReducedMotion();
  const animType = getAnimatedExperienceType(type);
  if (!animType) return <>{fallback ?? null}</>;

  // Canonical icon — same source of truth as <ExperienceTypeBadge>.
  const Icon = getExperienceTypeMeta(animType).Icon;
  const s = SIZES[size];

  if (reducedMotion) {
    return <Icon className={cn(s.icon, iconClassName, className)} style={style} aria-hidden />;
  }

  const sig = SIGNATURES[animType];
  const accent = accentClassName ?? ACCENTS[animType];

  return (
    <span
      className={cn("relative inline-flex items-center justify-center", className)}
      style={style}
      aria-hidden="true"
    >
      <AnimatePresence>
        {active && s.ripple && (
          <motion.span
            key="ripple"
            className={cn("pointer-events-none absolute border border-current", s.ripple)}
            initial={{ opacity: 0.5, scale: 0.55 }}
            animate={{ opacity: 0, scale: 1.5 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          />
        )}
      </AnimatePresence>

      <motion.span
        className="inline-flex"
        animate={{ y: active ? s.lift : 0 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
      >
        <motion.span
          className="inline-flex"
          animate={active ? sig.animate : { x: 0, rotate: 0, scale: 1 }}
          transition={
            active
              ? { duration: sig.duration, repeat: Infinity, ease: "easeInOut" }
              : { duration: 0.2 }
          }
        >
          <Icon className={cn(s.icon, iconClassName)} />
        </motion.span>
      </motion.span>

      {active && animType === "podcast" && (
        <>
          <span
            className={cn(
              "pointer-events-none absolute left-1/2 flex -translate-x-1/2 items-end",
              s.eq.gap,
              s.eq.offset,
            )}
          >
            {s.eq.bars.map((height, i) => (
              <motion.span
                key={i}
                className={cn("block origin-bottom rounded-full", s.eq.bar, accent)}
                style={{ height }}
                animate={{ scaleY: [0.22, 1, 0.22] }}
                transition={{
                  duration: BAR_DURATIONS[i % BAR_DURATIONS.length],
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: i * 0.09,
                }}
              />
            ))}
          </span>
          {s.notes.map((note, i) => (
            <motion.span
              key={`note${i}`}
              className="pointer-events-none absolute -top-1 left-1/2 select-none leading-none"
              style={{ fontSize: note.size }}
              initial={{ opacity: 0, x: 0, y: 2, scale: 0.6, rotate: note.x < 0 ? -12 : 10 }}
              animate={{
                opacity: [0, 0.95, 0.85, 0],
                x: [0, note.x * 0.6, note.x],
                y: [2, s.drift * 0.55, s.drift],
                scale: [0.6, 1, 1.05],
                rotate: [note.x < 0 ? -12 : 10, note.x < 0 ? 14 : -12],
              }}
              transition={{ duration: 1.7, repeat: Infinity, ease: "easeOut", delay: note.delay }}
            >
              {note.char}
            </motion.span>
          ))}
        </>
      )}

      {active && animType === "video" && (
        <>
          <motion.span
            className={cn("pointer-events-none absolute rounded-full", s.rec, accent)}
            animate={{ opacity: [1, 0.2, 1] }}
            transition={{ duration: 1.1, repeat: Infinity, ease: "easeInOut" }}
          />
          {s.frameDelays.map((delay, i) => (
            <motion.span
              key={`fr${i}`}
              className={cn("pointer-events-none absolute border border-current", s.frame)}
              initial={{ opacity: 0, x: 0, y: 0, scale: 0.5 }}
              animate={{
                opacity: [0, 0.9, 0],
                x: [0, s.frameDrift.x * 0.55, s.frameDrift.x],
                y: [0, s.frameDrift.y * 0.55, s.frameDrift.y],
                scale: [0.5, 1, 1.1],
              }}
              transition={{ duration: 1.3, repeat: Infinity, ease: "easeOut", delay }}
            />
          ))}
        </>
      )}

      {active && animType === "quest" && (
        <>
          {s.trailDots.map((p, i) => (
            <motion.span
              key={`tr${i}`}
              className={cn(
                "pointer-events-none absolute left-1/2 top-0 rounded-full",
                s.trailDot,
                accent,
              )}
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0, 1, 1, 0], scale: [0.3, 0.3, 1, 1, 0.8], x: p.x, y: p.y }}
              transition={{
                duration: TRAIL_CYCLE,
                repeat: Infinity,
                ease: "easeOut",
                times: [0, p.tIn - 0.02, p.tIn, 0.82, 1],
              }}
            />
          ))}
          <motion.span
            className="pointer-events-none absolute left-1/2 top-0 select-none leading-none"
            style={{ fontSize: s.flag.size }}
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0, 0, 1, 1, 0],
              scale: [0.2, 0.2, 1.25, 1, 0.8],
              rotate: [0, 0, -10, 0, 0],
              x: s.flag.x,
              y: s.flag.y,
            }}
            transition={{
              duration: TRAIL_CYCLE,
              repeat: Infinity,
              ease: "easeOut",
              times: [0, 0.5, 0.58, 0.82, 1],
            }}
          >
            ⚑
          </motion.span>
        </>
      )}

      {active && animType === "expert" && (
        <span
          className={cn(
            "pointer-events-none absolute left-1/2 flex -translate-x-1/2",
            s.typingOffset,
            s.typingGap,
          )}
        >
          {[0, 1, 2].map((i) => (
            <motion.span
              key={i}
              className={cn("block rounded-full", s.typingDot, accent)}
              animate={{ y: [0, s.typingBounce, 0], opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 0.7, repeat: Infinity, ease: "easeInOut", delay: i * 0.16 }}
            />
          ))}
        </span>
      )}

      {active && animType === "note" && (
        <>
          <motion.span
            className={cn("pointer-events-none absolute select-none leading-none", s.pencilPos)}
            style={{ fontSize: s.pencilSize }}
            animate={{ x: [-1.5, 1.5, -1.5], rotate: [-10, 2, -10] }}
            transition={{ duration: 0.6, repeat: Infinity, ease: "easeInOut" }}
          >
            ✎
          </motion.span>
          {s.sparkles.map((t, i) => (
            <motion.span
              key={`tw${i}`}
              className="pointer-events-none absolute select-none leading-none"
              style={{ ...t.style, fontSize: t.size }}
              initial={{ opacity: 0, scale: 0.3, rotate: 0 }}
              animate={{ opacity: [0, 1, 0], scale: [0.3, 1.05, 0.3], rotate: [0, 90] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut", delay: t.delay }}
            >
              ✦
            </motion.span>
          ))}
        </>
      )}
    </span>
  );
}
